
import { onAuthStateChanged } from "firebase/auth";
import { Bussiness } from "../bussiness/bussiness.entity";
import bussinessQuery, { BussinessQueryErrorKey } from "../bussiness/bussiness.query";
import { AppError, AppErrorKey } from "../common/app-error";
import ValueObjectStateify from "../common/vo/value-object-stateify";
import firebase from "./firebase";
import ValueObjectObservable from "../common/vo/value-object-observable";
import { collection } from "firebase/firestore";
import { doc, getDoc } from "firebase/firestore";
import { setDoc } from "firebase/firestore";
import Swal from "sweetalert2";
import { Print } from "./print";
import { ref } from "firebase/storage";
import { Permission, User } from "../users/user.entity";
import { userRepository } from "../users/user.repository";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppSettings } from "../settings/app-settings.entity";
import { appSettingsRepository } from "../settings/app-settings.repository";


class Application {
    static instance = new Application();
    public readonly user = new ValueObjectStateify<User | null>(null);
    public readonly authReady = new ValueObjectObservable<boolean>(false);
    public readonly printer = new ValueObjectObservable<Print | undefined>(undefined);
    public readonly bussiness = new ValueObjectStateify<Bussiness | undefined>(undefined);
    public readonly appSettings = new ValueObjectStateify<AppSettings>(AppSettings.defaults());

    private userUnsubscribe: (() => void) | undefined;



    private constructor() {
        onAuthStateChanged(firebase.auth, async (user) => {
            if (!user) {
                if (this.userUnsubscribe) this.userUnsubscribe();
                this.userUnsubscribe = undefined;
                this.user.set(null);
                this.appSettings.set(AppSettings.defaults());
                this.authReady.set(true);
                return
            }
            try {
                try {
                    await this.loadBussinessForUser(user.uid);
                    await this.loadAppSettings();
                } catch (bErr) {
                    this.bussiness.set(undefined);
                }

                this.user.set(await userRepository.findById(user.uid));

                const [userObservable, userUnsubscribe] = userRepository.findByIdObservable(user.uid);
                userObservable.addEventListener((user) => {
                    if (!user) return;
                    if (user === this.user.get()) return;
                    this.user.set(user);
                });

                this.userUnsubscribe = userUnsubscribe;
            } catch (err) {
                console.error(err);
                this.user.set(null);
            } finally {
                this.authReady.set(true);
            }
        });
    }

    private async loadBussinessForUser(uid: string) {
        const membershipSnap = await getDoc(doc(firebase.db, "user_businesses", uid));
        const businessKey = membershipSnap.data()?.businessKey;
        if (!businessKey || typeof businessKey !== "string") {
            this.bussiness.set(undefined);
            return;
        }
        const bussiness = await bussinessQuery.queryByKey(businessKey);
        this.bussiness.set(bussiness);
    }

    async loadBussiness() {
        const uid = firebase.auth.currentUser?.uid;
        if (!uid) throw new AppError(AppErrorKey.BUSSINESS_NOT_DEFINED);
        await this.loadBussinessForUser(uid);
    };

    async loadAppSettings() {
        if (!this.bussiness.get()) {
            return;
        }
        try {
            const settings = await appSettingsRepository.getConfig();
            this.appSettings.set(settings);
        } catch {
            this.appSettings.set(AppSettings.defaults());
        }
    }

    async initializePrint() {
        const print = new Print();

        try {
            await print.configure();
        } catch (err) {
            return;
        }

        this.printer.set(print);
    }

    getCollection(collectionName: string) {
        const bussiness = this.bussiness.get();

        if (!bussiness) {
            throw new AppError(AppErrorKey.BUSSINESS_NOT_DEFINED);
        }

        return collection(firebase.db, `${bussiness.key}/items/${collectionName}`)
    }

    getStorageRef(path: string) {
        const bussiness = this.bussiness.get();

        if (!bussiness) {
            throw new AppError(AppErrorKey.BUSSINESS_NOT_DEFINED);
        }

        return ref(firebase.storage, `${bussiness.key}/${path}`);
    }

    getPrint(): Print {
        const printer = this.printer.get();
        if (!printer) throw new AppError(AppErrorKey.PRINTER_NOT_INITIALIZED);

        return printer;
    }

    get useHasPermission() {
        return (permission: Permission | Permission[]) => {
            const navigate = useNavigate();

            const [user] = this.user.useState();

            useEffect(() => {
                if (!user) return navigate("/login");

                if (!user.hasPermission(permission)) {
                    navigate('/403');
                }
            }, [user]);
        };
    }
}

export default Application.instance;